import React, { useContext, useEffect, useRef, useState } from 'react'
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { useDispatch, useSelector } from "react-redux";
import { cloneDeep } from 'lodash';
import { genKey, getSelectedNodeWhileCreation, getUniqueWorkflowItemIdFromArray } from '../../../utils/utilFunctions';
import { treeActions } from '../../../redux/slices/treeSlice';
import { getRecordInsertingPosition } from './CopyAndPaste';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

function NewSendData({ openModal, toggle }) {
  const { t } = useTranslation()
  const parentScreen = useContext(ParentScreenCtx)
  const { rightTree } = useSelector(state => state.treeData[parentScreen]);
  const { templateHeader } = useSelector(state => state.treeData.workflow.rightTree.tree)
  const currentTab = rightTree.currentTab;
  const selectedNode = rightTree.tree[currentTab].selectedNode;
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false)
  const contentRef = useRef();
  const content = t('BOX_LIKE_TO_PLACE_ELEM_INSIDE_CURR_ELEM_OR_DIRECTLY_AFTER').split(`\\n`).join("<br />")

  const handleNewSendData = (isInside) => {
    const exactNode = getSelectedNodeWhileCreation(selectedNode, rightTree?.tree?.[currentTab]?.translatedData || [], isInside);
    let nestedLevels = exactNode.key.split("-");
    nestedLevels = nestedLevels.splice(1);
    const translatedData = cloneDeep(rightTree.tree[currentTab].translatedData);
    let initialId = translatedData?.[0]?.ID;
    const newId = getUniqueWorkflowItemIdFromArray(translatedData);

    const getNewSendData = (data, key, isInside) => {
      const title = getNewName(rightTree.tree)
      if (!data.length) {
        return { key: `${key}-0`, title, placingPosition: 1 };
      }

      let placingPosition;
      let newSendDataKey;

      const details = getRecordInsertingPosition(
        data,
        exactNode,
        isInside,
        "workflow"
      );
      placingPosition = details.index;
      newSendDataKey = details.key;
      return { key: newSendDataKey, title, placingPosition: placingPosition };
    }

    const insertNodeInNestedTree = (translatedData, nestedLevels, isInside) => {
      const level = isInside ? 0 : 1;
      if (nestedLevels.length === level) {
        const { children = [], key } = translatedData;
        const newSendDataDetails = children ? getNewSendData(children, key, isInside) : getNewSendData(translatedData, 'wf', isInside);
        const archiveData = templateHeader?.WORKFLOW?.ARCHIVE_TRANSACTION_FILES;
        const newNode = {
          key: newSendDataDetails.key,
          title: newSendDataDetails.title,
          nodeId: genKey(newSendDataDetails.title),
          alternateNodeId: genKey(newSendDataDetails.title),
          ICON_DISPLAY: 'SEND_DATA',
          ACTION: 'SEND_DATA',
          ARCHIVE_TRANSACTION_FILES: archiveData === 'false' ? "FALSE" : "TRUE",
          CONCURRENT: 'FALSE',
          ENABLED: 'TRUE',
          ID: newId,
          LOG_WFITEM_DETAILS: 'TRUE',
          NAME: newSendDataDetails.title,
          OUTBOUND_ID: newId + 1,
          OUTBOUND_NAME: newSendDataDetails.title,
          SOURCE_FILE_DO_NOT_DELETE: 'FALSE',
          SOURCE_FILE_PRESERVE_PATHS: 'FALSE',
          SOURCE_TYPE: 'TRANSLATED_DATA',
          SYNCHRONOUS: 'FALSE',
          OUTBOUND: {
            ADAPTER_TYPE: 'FILE',
            APPEND_FILE: 'FALSE',
            ENCODING: 'UTF-8',
            ID: newId + 1
          }
        }
        dispatch(
          treeActions.setRightTreeSelectedNode({
            screen: parentScreen,
            type: currentTab,
            data: {},
          })
        );
        const translatedDataArray = children ? translatedData.children : translatedData;
        translatedDataArray?.splice(newSendDataDetails.placingPosition, 0, newNode);
        setTimeout(() => {
          dispatch(treeActions.setRightTreeSelectedNode({
            screen: parentScreen,
            type: currentTab,
            data: { ...newNode }
          }));
        })
        return;
      }
      const isArray = Array.isArray(translatedData);

      if (isArray) {
        const nestedData = translatedData[[nestedLevels[0]]];
        const newNestedLevels = nestedLevels.splice(1);
        insertNodeInNestedTree(nestedData, newNestedLevels, isInside);
      } else {
        insertNodeInNestedTree(translatedData?.children, nestedLevels, isInside);
      }
    }

    insertNodeInNestedTree(translatedData, nestedLevels, isInside);
    const updatedData = getUpdatedData(translatedData, initialId);
    dispatch(treeActions.setNewRightTreeTranslatedData({
      screen: parentScreen,
      type: currentTab,
      data: updatedData
    }));
    toggle(prev => null);
    setShowModal(false);
  }

  const newSendDataHandler = () => {
    if (['LOOP_ITEM', 'CONDITION_IF', 'CONDITION_ELSE'].includes(selectedNode.ICON_DISPLAY) && openModal) {
      setShowModal(true);
    } else if (selectedNode.key === 'wf-0' && selectedNode.ICON_DISPLAY === 'OBJECTS') {
      handleNewSendData(true);
      return;
    } else {
      handleNewSendData(false);
      return;
    }
  }

  setTimeout(() => {
    if (contentRef.current?.innerHTML === "") contentRef.current.innerHTML = content
  }, 0)

  useEffect(() => {
    if (openModal) newSendDataHandler();
  }, [openModal]);

  return (
    <>
      <Dialog open={showModal} maxWidth="xs" fullWidth>
        <DialogTitle className='dialog-header'>
          <div className='title'>{t('BOX_CONFIRM')}</div>
        </DialogTitle>
        <DialogContent className='dialog-content' dividers>
          <div ref={contentRef}></div>
        </DialogContent>
        <DialogActions>
          <Button size='small' variant='outlined' color="primary" onClick={() => handleNewSendData(true)}>
            {t('BTN_YES')}
          </Button>
          <Button size='small' variant='outlined' color="primary" onClick={() => handleNewSendData(false)}>
            {t('BTN_NO')}
          </Button>
          <Button size='small' variant='outlined' color="primary" onClick={() => {
            toggle(null);
            setShowModal(false)
          }}>
            {t('BTN_CANCEL')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default NewSendData

export const getNewName = (tree) => {
  const data = cloneDeep(tree);
  const body = data.body.translatedData;
  let titleNumber = 0;
  let hasPlainSendData = false;

  const getTitleName = (data) => {
    data.forEach((ele) => {
      if (ele?.title?.startsWith("Send Data")) {
        const title = ele.title;
        const openParenIndex = title.indexOf("(");
        const closeParenIndex = title.indexOf(")");

        if (openParenIndex > -1 && closeParenIndex > openParenIndex) {
          const numberPart = title.slice(openParenIndex + 1, closeParenIndex).trim();
          const parsedNumber = parseInt(numberPart, 10);

          if (!isNaN(parsedNumber)) {
            titleNumber = Math.max(titleNumber, parsedNumber);
          }
        } else {
          hasPlainSendData = true;
        }
      }

      if (ele?.children?.length > 0) {
        getTitleName(ele.children);
      }
    });
  };

  getTitleName(body);

  if (hasPlainSendData && titleNumber === 0) {
    return "Send Data (1)";
  }

  return titleNumber === 0 ? "Send Data" : `Send Data (${titleNumber + 1})`;
};

export const getUpdatedData = (translatedData, initialId) => {
  const data = cloneDeep(translatedData);
  const key = 'wf'

  const getNewKeys = (data, key) => {
    data.forEach((ele, idx) => {
      const newKey = `${key}-${idx}`
      if (ele.children?.length > 0) {
        if (data[idx]?.isLeaf !== undefined) {
          delete data[idx]?.isLeaf
        }
        getNewKeys(ele.children, newKey);
      }
    })
  }

  getNewKeys(data, key);
  return data;
} 